import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const MukiContainer = styled.div`
  min-height: 100vh;
  padding-top: 100px;
`;

const HeroSection = styled.section`
  position: relative;
  height: 20vh;
  background: ${props => props.theme.colors.gradients.muki};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  overflow: hidden;
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-family: ${props => props.theme.fonts.script};
  font-size: clamp(3rem, 6vw, 5rem);
  font-weight: 700;
  margin: 0;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.4);
`;

const ContentSection = styled.section`
  padding: 4rem 0;
  background: ${props => props.theme.colors.backgroundLight};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const InfoContent = styled.div`
  h3 {
    font-size: 2rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: ${props => props.theme.colors.textLight};
    margin-bottom: 1.5rem;
  }
  
  .highlight-box {
    background: white;
    padding: 2rem;
    border-radius: 15px;
    margin: 2rem 0;
    box-shadow: ${props => props.theme.shadows.sm};
    
    h4 {
      color: ${props => props.theme.colors.primary};
      margin-bottom: 1rem;
    }
    
    ul {
      list-style: none;
      padding: 0;
      
      li {
        padding: 0.5rem 0;
        
        &::before {
          content: '👶';
          margin-right: 0.5rem;
        }
      }
    }
  }
`;

const InfoSidebar = styled.div`
  .info-card {
    background: white;
    padding: 2rem;
    border-radius: 20px;
    box-shadow: ${props => props.theme.shadows.card};
    margin-bottom: 2rem;
    
    h4 {
      color: ${props => props.theme.colors.primary};
      margin-bottom: 1rem;
      font-size: 1.3rem;
    }
    
    .info-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 0;
      border-bottom: 1px solid ${props => props.theme.colors.lightGray};
      
      &:last-child {
        border-bottom: none;
      }
      
      .label {
        color: ${props => props.theme.colors.textLight};
      }
      
      .value {
        font-weight: 600;
        color: ${props => props.theme.colors.text};
      }
    }
  }
`;

const PricingSection = styled.section`
  padding: 4rem 0;
  background: white;
`;

const BenefitsSection = styled.section`
  padding: 4rem 0;
  background: ${props => props.theme.colors.gradients.card};
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const BenefitCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  text-align: center;
  box-shadow: ${props => props.theme.shadows.card};
  
  .icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }
  
  h4 {
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
  }
  
  p {
    color: ${props => props.theme.colors.textLight};
    line-height: 1.6;
  }
`;

const BookingButton = styled(Link)`
  display: inline-block;
  padding: 1rem 2rem;
  background: linear-gradient(45deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  text-decoration: none;
  border-radius: 25px;
  font-weight: 600;
  text-align: center;
  transition: all 0.3s ease;
  margin-top: 2rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(78, 205, 196, 0.4);
  }
`;

const MukiClub = () => {

  const benefits = [
    {
      icon: '👥',
      title: 'Gemeinsame Erlebnisse',
      description: 'Eltern und Kinder erleben zusammen die Faszination der Ponys und stärken ihre Bindung.'
    },
    {
      icon: '🧠',
      title: 'Frühe Entwicklung',
      description: 'Motorik, Koordination und Sinneswahrnehmung werden spielerisch gefördert.'
    },
    {
      icon: '❤️',
      title: 'Emotionale Bindung',
      description: 'Erste Beziehung zu Tieren aufbauen und Empathie entwickeln.'
    },
    {
      icon: '🤝',
      title: 'Soziale Kompetenz',
      description: 'In der Gruppe mit anderen Familien wichtige soziale Fähigkeiten erlernen.'
    }
  ];

  return (
    <MukiContainer>
      <Helmet>
        <title>MuKi-Club - Der Ponystall | Für Kleinkinder ab 2 Jahren mit Begleitperson</title>
        <meta name="description" content="MuKi-Club für Kinder ab 2 Jahren mit Mama oder Papa - gemeinsame Pony-Erlebnisse, frühe Entwicklungsförderung und wertvolle Familienzeit." />
      </Helmet>

      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            MuKi-Club
          </HeroTitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <Container>
          <InfoGrid>
            <InfoContent>
              <h3>Gemeinsame Erlebnisse für die ganze Familie</h3>
              <p>
                Der MuKi-Club ist ein ganz besonderes Angebot für Kleinkinder ab 2 Jahren 
                zusammen mit Mama, Papa, Oma, Opa oder einer anderen Begleitperson. 
                Hier können die Allerkleinsten ihre ersten Erfahrungen mit Ponys sammeln.
              </p>
              <p>
                In ruhiger und liebevoller Atmosphäre nähern sich Kind und Begleitperson 
                gemeinsam den sanften Ponys. Die Stunden sind speziell auf die Bedürfnisse 
                von Kleinkindern abgestimmt - mit viel Zeit zum Entdecken, Staunen und Kuscheln.
              </p>

              <div className="highlight-box">
                <h4>Das bietet der MuKi-Club:</h4>
                <ul>
                  <li>Erste sanfte Begegnung mit Ponys</li>
                  <li>Gemeinsame Zeit für Eltern und Kind</li>
                  <li>Spielerische Motorik-Förderung</li>
                  <li>Sinneswahrnehmung schulen</li>
                  <li>Vertrauen und Mut entwickeln</li>
                  <li>Soziale Kontakte zu anderen Familien</li>
                  <li>Entspannende Auszeit vom Alltag</li>
                </ul>
              </div>
            </InfoContent>

            <InfoSidebar>
              <div className="info-card">
                <h4>Wichtige Informationen</h4>
                <div className="info-item">
                  <span className="label">Alter:</span>
                  <span className="value">Ab 2 Jahren</span>
                </div>
                <div className="info-item">
                  <span className="label">Begleitung:</span>
                  <span className="value">1 Erwachsener</span>
                </div>
                <div className="info-item">
                  <span className="label">Gruppengröße:</span>
                  <span className="value">Max. 8</span>
                </div>

              </div>

              <div className="info-card">
                <h4>Für wen geeignet?</h4>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  <li>👶 Kleinkinder ab 2 Jahren</li>
                  <li>👨‍👩‍👧 Familien mit kleinen Kindern</li>
                  <li>👵 Großeltern mit Enkeln</li>
                  <li>💕 Alle, die gemeinsame Tiererlebnisse suchen</li>
                </ul>
              </div>
            </InfoSidebar>
          </InfoGrid>
        </Container>
      </ContentSection>

      <BenefitsSection>
        <Container>
          <div className="text-center">
            <h2 className="section-title">Warum MuKi-Club?</h2>
            <p className="section-subtitle">
              Die positiven Auswirkungen der tiergestützten Frühförderung
            </p>
          </div>

          <BenefitsGrid>
            {benefits.map((benefit, index) => (
              <BenefitCard
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <div className="icon">{benefit.icon}</div>
                <h4>{benefit.title}</h4>
                <p>{benefit.description}</p>
              </BenefitCard>
            ))}
          </BenefitsGrid>
        </Container>
      </BenefitsSection>

      <PricingSection>
        <Container>
          <div className="text-center">
            <h2 className="section-title">MuKi-Club buchen</h2>
            <p className="section-subtitle">
              Alle aktuellen Kursangebote und Buchungsmöglichkeiten finden Sie auf unserer Kursübersicht.
            </p>
          </div>

          <div className="text-center" style={{ marginTop: '3rem' }}>
            <BookingButton to="/kurse">
              Zu den Buchungsoptionen
            </BookingButton>
          </div>
        </Container>
      </PricingSection>
    </MukiContainer>
  );
};

export default MukiClub;